import type { RequestOptions } from '@@/plugin-request/request';
import type { RequestConfig } from '@umijs/max';
import { message, notification } from 'antd';

// 是否已发起登录页跳转，避免多个并发请求重复提示与跳转
let redirectingToLogin = false;

/**
 * 是否为登录接口请求。登录接口自身的 401（如密码错误）只提示错误信息，不跳转登录页
 */
const isLoginRequest = (error: any) =>
  (error.config?.url || '').includes('/api/login/account');

/**
 * 处理 401 未认证（token 失效/过期）：清除本地凭证，提示后跳转登录页
 */
const handleUnauthorized = () => {
  if (redirectingToLogin || window.location.pathname === '/user/login') {
    return;
  }
  redirectingToLogin = true;
  localStorage.removeItem('token');
  message.error('登录状态已失效，请重新登录');
  const redirect = encodeURIComponent(
    window.location.pathname + window.location.search,
  );
  // 稍作延迟跳转，让提示信息先展示出来
  setTimeout(() => {
    window.location.href = `/user/login?redirect=${redirect}`;
  }, 800);
};

// 错误处理方案： 错误类型
enum ErrorShowType {
  SILENT = 0,
  WARN_MESSAGE = 1,
  ERROR_MESSAGE = 2,
  NOTIFICATION = 3,
  REDIRECT = 9,
}
// 与后端约定的响应数据格式
interface ResponseStructure {
  success: boolean;
  data: any;
  code?: number;
  msg?: string;
  errorCode?: number;
  errorMessage?: string;
  showType?: ErrorShowType;
}

/**
 * @name 错误处理
 * pro 自带的错误处理， 可以在这里做自己的改动
 * @doc https://umijs.org/docs/max/request#配置
 */
export const errorConfig: RequestConfig = {
  // 错误处理： umi@3 的错误处理方案。
  errorConfig: {
    // 错误抛出
    errorThrower: (res) => {
      const { success, data, errorCode, errorMessage, showType } =
        res as unknown as ResponseStructure;
      if (!success) {
        const error: any = new Error(errorMessage);
        error.name = 'BizError';
        error.info = { errorCode, errorMessage, showType, data };
        throw error; // 抛出自制的错误
      }
    },
    // 错误接收及处理
    errorHandler: (error: any, opts: any) => {
      if (opts?.skipErrorHandler) throw error;
      // 我们的 errorThrower 抛出的错误。
      if (error.name === 'BizError') {
        const errorInfo: ResponseStructure | undefined = error.info;
        if (errorInfo) {
          const { errorMessage, errorCode } = errorInfo;
          switch (errorInfo.showType) {
            case ErrorShowType.SILENT:
              // do nothing
              break;
            case ErrorShowType.WARN_MESSAGE:
              message.warning(errorMessage);
              break;
            case ErrorShowType.ERROR_MESSAGE:
              message.error(errorMessage);
              break;
            case ErrorShowType.NOTIFICATION:
              notification.open({
                description: errorMessage,
                message: errorCode,
              });
              break;
            case ErrorShowType.REDIRECT:
              // TODO: redirect
              break;
            default:
              message.error(errorMessage);
          }
        }
      } else if (error.response) {
        // Axios 的错误
        // 请求成功发出且服务器也响应了状态码，但状态代码超出了 2xx 的范围
        // 响应拦截器已处理业务错误消息，此处不再重复提示
      } else if (error.request) {
        // 请求已经成功发起，但没有收到响应
        // \`error.request\` 在浏览器中是 XMLHttpRequest 的实例，
        // 而在node.js中是 http.ClientRequest 的实例
        message.error('None response! Please retry.');
      } else {
        // 发送请求时出了点问题
        message.error('Request error, please retry.');
      }
    },
  },

  // 请求拦截器
  requestInterceptors: [
    (config: RequestOptions) => {
      // 拦截请求配置，进行个性化处理
      const url = config?.url || '';
      // 非登录接口添加 Authorization header
      if (!url.includes('/api/login/account')) {
        const token = localStorage.getItem('token');
        if (token) {
          config.headers = {
            ...config.headers,
            Authorization: `Bearer ${token}`,
          };
        }
      }
      return { ...config, url };
    },
  ],

  // 响应拦截器
  responseInterceptors: [
    [
      (response) => {
        // 拦截响应数据，进行个性化处理
        const { data } = response as unknown as ResponseStructure;

        if (data?.success === false) {
          message.error('请求失败！');
        }
        return response;
      },
      // HTTP 错误拦截器 - 处理非 2xx 状态码
      (error: any) => {
        if (error.response) {
          // 请求已发出，但服务器响应了非 2xx 状态码
          const responseData = error.response.data as ResponseStructure;
          // 401 未认证（token 失效/过期）：清除本地凭证并跳转登录页
          if (error.response.status === 401 && !isLoginRequest(error)) {
            handleUnauthorized();
          } else if (responseData?.msg) {
            message.error(responseData.msg);
          } else {
            message.error(`请求失败，状态码: ${error.response.status}`);
          }
        } else if (error.request) {
          // 请求已发出，但没有收到响应
          message.error('网络错误，请检查后端服务是否启动！');
        } else {
          // 发送请求时出错
          message.error('请求发送失败，请重试！');
        }
        return Promise.reject(error);
      },
    ],
  ],
};
